import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ListTodo, Search, Plus, Loader2, Pencil, Check, X, Trash2, ChevronDown, ChevronUp, Clock, CircleCheck, Ban, CircleDot } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUTS = {
    a_traiter: { label: 'À traiter', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: CircleDot, iconColor: 'text-amber-500', iconBg: 'bg-amber-50' },
    termine: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CircleCheck, iconColor: 'text-emerald-500', iconBg: 'bg-emerald-50' },
};

const STATUT_FALLBACK = { label: 'Ancien', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: CircleDot, iconColor: 'text-slate-400', iconBg: 'bg-slate-50' };

function formatDate(d) { if (!d) return '-'; try { return format(new Date(d), 'dd/MM/yyyy HH:mm', { locale: fr }); } catch { return d; } }

export default function TachesPage() {
    const [taches, setTaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatut, setFilterStatut] = useState('all');
    const [filterPersonne, setFilterPersonne] = useState('all');
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ client_nom: '', titre: '', personne_concernee: '', remarque: '', statut: 'a_effectuer' });
    const [editingRemarque, setEditingRemarque] = useState(null);
    const [editRemarqueValue, setEditRemarqueValue] = useState('');
    const [users, setUsers] = useState([]);
    const [expandedRemarques, setExpandedRemarques] = useState({});

    const fetchData = () => {
        setLoading(true);
        Promise.all([
            axios.get(`${API}/api/taches`, { withCredentials: true }),
            axios.get(`${API}/api/admin/users`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]).then(([tRes, uRes]) => {
            setTaches(tRes.data);
            setUsers(uRes.data || []);
        }).catch(() => toast.error('Erreur de chargement'))
        .finally(() => setLoading(false));
    };
    useEffect(() => { fetchData(); }, []);

    const handleCreate = async () => {
        if (!form.titre || !form.personne_concernee) {
            toast.error('Veuillez remplir le titre et la personne concernée');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/api/taches`, form, { withCredentials: true });
            toast.success('Tâche créée');
            setShowForm(false);
            setForm({ client_nom: '', titre: '', personne_concernee: '', remarque: '', statut: 'a_effectuer' });
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    const handleStatut = async (id, statut) => {
        try {
            await axios.put(`${API}/api/taches/${id}`, { statut }, { withCredentials: true });
            toast.success('Statut mis à jour');
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    const handleSaveRemarque = async (id) => {
        try {
            await axios.put(`${API}/api/taches/${id}`, { remarque: editRemarqueValue }, { withCredentials: true });
            toast.success('Remarque mise à jour');
            setEditingRemarque(null);
            fetchData();
        } catch { toast.error('Erreur'); }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${API}/api/taches/${id}`, { withCredentials: true });
            toast.success('Tâche supprimée');
            fetchData();
        } catch { toast.error('Erreur'); }
    };

    // Build personnes list from taches + users
    const personnes = [...new Set([
        ...taches.map(t => t.personne_concernee).filter(Boolean),
        'Admin', 'Assistante',
        ...users.map(u => u.name).filter(Boolean),
    ])];

    const filtered = taches.filter(t => {
        if (filterStatut !== 'all' && t.statut !== filterStatut) return false;
        if (filterPersonne !== 'all' && t.personne_concernee !== filterPersonne) return false;
        if (search) {
            const s = search.toLowerCase();
            return (t.client_nom || '').toLowerCase().includes(s) || (t.titre || '').toLowerCase().includes(s) || (t.personne_concernee || '').toLowerCase().includes(s);
        }
        return true;
    });

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="taches-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Rappels importants</h1>
                    <p className="mt-1 text-slate-500">{filtered.length} rappel{filtered.length > 1 ? 's' : ''}</p>
                </div>
            </div>

            {/* Filters */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="flex flex-wrap gap-3">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="tache-search" />
                        </div>
                        <Select value={filterStatut} onValueChange={setFilterStatut}>
                            <SelectTrigger className="w-[160px]" data-testid="tache-filter-statut">
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                {Object.entries(STATUTS).map(([v, info]) => (
                                    <SelectItem key={v} value={v}>{info.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterPersonne} onValueChange={setFilterPersonne}>
                            <SelectTrigger className="w-[180px]" data-testid="tache-filter-personne">
                                <SelectValue placeholder="Personne" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes les personnes</SelectItem>
                                <SelectItem value="Admin">Admin</SelectItem>
                                <SelectItem value="Assistante">Assistante</SelectItem>
                                {users.filter(u => u.name && u.role === 'commercial').map(u => (
                                    <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <ListTodo size={20} className="text-[#2e7cd6]" />
                        Rappels importants ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">Aucune tâche</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table className="min-w-[700px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[160px]">Tâche</TableHead>
                                        <TableHead className="w-[110px]">Personne</TableHead>
                                        <TableHead className="w-[120px]">Client</TableHead>
                                        <TableHead>Remarque</TableHead>
                                        <TableHead className="w-[140px]">Statut</TableHead>
                                        <TableHead className="text-right w-[50px]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(t => {
                                        const sInfo = STATUTS[t.statut] || STATUT_FALLBACK;
                                        const remarqueText = t.remarque || '';
                                        const isLong = remarqueText.length > 60;
                                        const isExpanded = expandedRemarques[t.id];
                                        return (
                                            <TableRow key={t.id} data-testid={`tache-row-${t.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center gap-2.5">
                                                        <div className={`w-8 h-8 rounded-lg ${sInfo.iconBg} flex items-center justify-center flex-shrink-0`}>
                                                            <sInfo.icon size={16} className={sInfo.iconColor} />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-slate-800 leading-tight">{t.titre}</p>
                                                            <span className="text-[10px] text-slate-400 mt-0.5 block">{formatDate(t.created_at)}</span>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{t.personne_concernee}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <p className="font-medium text-slate-900 text-sm">{t.client_nom || '-'}</p>
                                                </TableCell>
                                                <TableCell>
                                                    {editingRemarque === t.id ? (
                                                        <div className="flex items-center gap-1">
                                                            <Textarea
                                                                value={editRemarqueValue}
                                                                onChange={e => setEditRemarqueValue(e.target.value)}
                                                                className="text-xs min-h-[60px] resize-none"
                                                                autoFocus
                                                                onKeyDown={e => { if (e.key === 'Escape') setEditingRemarque(null); }}
                                                                data-testid={`tache-remarque-edit-${t.id}`}
                                                            />
                                                            <div className="flex flex-col gap-1">
                                                                <button onClick={() => handleSaveRemarque(t.id)} className="text-emerald-600 hover:text-emerald-700"><Check size={14} /></button>
                                                                <button onClick={() => setEditingRemarque(null)} className="text-slate-400 hover:text-slate-600"><X size={12} /></button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-start gap-1.5">
                                                            <div className="flex-1 min-w-0">
                                                                {remarqueText ? (
                                                                    <>
                                                                        <span className="text-xs text-slate-600 whitespace-pre-wrap break-words">
                                                                            {isLong && !isExpanded ? remarqueText.slice(0, 60) + '...' : remarqueText}
                                                                        </span>
                                                                        {isLong && (
                                                                            <button
                                                                                onClick={() => setExpandedRemarques(prev => ({ ...prev, [t.id]: !prev[t.id] }))}
                                                                                className="text-[10px] text-[#2e7cd6] hover:underline ml-1 inline-flex items-center gap-0.5"
                                                                                data-testid={`tache-voir-plus-${t.id}`}
                                                                            >
                                                                                {isExpanded ? <><ChevronUp size={10} /> voir moins</> : <><ChevronDown size={10} /> voir plus</>}
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <span className="text-xs text-slate-400 italic">-</span>
                                                                )}
                                                            </div>
                                                            <button onClick={() => { setEditingRemarque(t.id); setEditRemarqueValue(t.remarque || ''); }} className="text-slate-400 hover:text-[#2e7cd6] transition-colors flex-shrink-0 mt-0.5" data-testid={`tache-remarque-btn-${t.id}`}>
                                                                <Pencil size={12} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={`${sInfo.color} text-xs border`} data-testid={`tache-statut-${t.id}`}>{sInfo.label}</Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDelete(t.id)} data-testid={`tache-delete-${t.id}`}>
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
